import type {
  CellManifest,
  CellProofBundleView,
  DemoCell,
  DemoWorld,
  PhaArtifact,
  ReconstructionArtifactView,
  ReconstructionBrowserReport,
  RejectionResult,
  Rootprint,
  RootprintBranch,
  ValidationLocusArtifactView,
  ValidationLocusBrowserReport,
  VerificationReport,
} from "./types";

const encoder = new TextEncoder();
const CELL_DOMAIN = encoder.encode("TESSARYN-CELL-v0\0");
const CHUNK_DOMAIN = encoder.encode("TESSARYN-CHUNK-v0\0");
const MERKLE_LEAF_DOMAIN = encoder.encode("TESSARYN-MERKLE-LEAF-v0\0");
const MERKLE_NODE_DOMAIN = encoder.encode("TESSARYN-MERKLE-NODE-v0\0");
const EMPTY_MERKLE_DOMAIN = encoder.encode("TESSARYN-MERKLE-EMPTY-v0\0");
const PHA_DOMAIN = encoder.encode("power-house:pha:v1:phx-fingerprint\0");
const BRANCH_DOMAIN = encoder.encode("power-house:rootprint:v1:branch-id\0");
const REPLAY_DOMAIN = encoder.encode("power-house:rootprint:v1:replay-state\0");
const CAPSULE_DOMAIN = encoder.encode("PHM-CAPSULE-v1\0");
const CORE_DOMAIN = encoder.encode("PHM-CORE-v1\0");
const SIDECAR_DOMAIN = encoder.encode("power-house:observatory-sidecar:v1\0");
const SEMANTIC_DOMAIN = encoder.encode("PHM-SEMANTIC-PACKET-v1\0");
const SURFEL_MAGIC = encoder.encode("TESSARYN-SURFEL-v0\0");
const SDF_MAGIC = encoder.encode("TESSARYN-SDF-v0\0");
const TARTANAIR_PROFILE = {
  assets: [
    {
      bytes: 186_046_186,
      role: "depth",
      sha256:
        "sha256:83e6e680297af35aa83d594ea3ed254bf71e9d9da7b26fee6d0ccb29f25ac104",
      url: "https://huggingface.co/datasets/theairlabcmu/tartanair2/resolve/0d2d145e973832742a2aaa04b7d2ebffc8d82817/ArchVizTinyHouseDay/Data_easy/depth_lcam_front.zip",
    },
    {
      bytes: 517_844_269,
      role: "rgb",
      sha256:
        "sha256:9bea5fca9d0cf50105c7d34583d4d5db06e3715ef708262b4dfad763d34b17da",
      url: "https://huggingface.co/datasets/theairlabcmu/tartanair2/resolve/0d2d145e973832742a2aaa04b7d2ebffc8d82817/ArchVizTinyHouseDay/Data_easy/image_lcam_front.zip",
    },
  ],
  citation:
    "W. Wang, Y. Hu, Y. Qiu, S. Shen, and Y. Shaoul, TartanAir V2 Dataset, Carnegie Mellon University, 2023",
  dataset: "TartanAir V2",
  environment: "ArchVizTinyHouseDay",
  ground_truth: {
    camera_pose: true,
    metric_depth: true,
    optical_flow: false,
    reference: "simulator-exact",
    semantics: false,
  },
  homepage: "https://tartanair.org/",
  id: "tartanair-v2/archviz-tiny-house-day/easy/p000",
  license: "CC-BY-4.0",
  modalities: ["camera_pose", "metric_depth", "rgb"],
  release: "v2-2023",
  schema: "tessaryn/dataset-profile/v1",
  sensor: {
    coordinate_frame: "ned/opencv-camera",
    cx_q20: 335_544_320,
    cy_q20: 335_544_320,
    fx_q20: 335_544_320,
    fy_q20: 335_544_320,
    height: 640,
    sample_rate_millihz: 10_000,
    width: 640,
  },
  sequence: "Data_easy/P000/lcam_front",
  source_class: "synthetic_ground_truth",
} as const;

export async function calculateCellId(manifest: CellManifest): Promise<string> {
  const canonical = canonicalizeManifest(manifest);
  return hashDomain(CELL_DOMAIN, encoder.encode(canonicalStringify(canonical)));
}

export async function calculateChunkId(bytes: Uint8Array): Promise<string> {
  return hashDomain(CHUNK_DOMAIN, bytes);
}

export async function calculateCanonicalChunkId(value: unknown): Promise<string> {
  return hashDomain(CHUNK_DOMAIN, encoder.encode(canonicalStringify(value)));
}

export async function calculateChunkMerkleRoot(chunks: readonly string[]): Promise<string> {
  if (chunks.length === 0) return hashDomain(EMPTY_MERKLE_DOMAIN, new Uint8Array());
  let level = await Promise.all(
    chunks.map((chunk) => hashDomainBytes(MERKLE_LEAF_DOMAIN, digestBytes(chunk))),
  );
  level.sort(compareBytes);
  while (level.length > 1) {
    const next: Uint8Array[] = [];
    for (let index = 0; index < level.length; index += 2) {
      const left = level[index];
      const right = level[index + 1];
      if (!left) throw new Error("Cell Merkle level is empty");
      next.push(
        right
          ? await hashDomainBytes(MERKLE_NODE_DOMAIN, concatenate([left, right]))
          : left,
      );
    }
    level = next;
  }
  const root = level[0];
  if (!root) throw new Error("Cell Merkle root is empty");
  return "sha256:" + toHex(root);
}

export async function verifyCellProofBundle(proof: CellProofBundleView): Promise<{
  cellValid: boolean;
  phaValid: boolean;
  rootprintValid: boolean;
  replayValid: boolean;
  memoryValid: boolean;
  errors: string[];
}> {
  const report = {
    cellValid: false,
    phaValid: false,
    rootprintValid: false,
    replayValid: false,
    memoryValid: false,
    errors: [] as string[],
  };
  try {
    report.cellValid = (await calculateCellId(proof.manifest)) === proof.cell_id;
    if (!report.cellValid) throw new Error("Cell identity mismatch");
    report.phaValid =
      (await calculatePhaFingerprint(proof.pha)) === proof.pha.phx_fingerprint &&
      proof.pha.embedded_proof.protocol === "tessaryn/world-cell/v0" &&
      proof.pha.embedded_proof.public_inputs.cell_manifest_digest === proof.cell_id &&
      proof.pha.identity_root === proof.rootprint.root_branch;
    if (!report.phaValid) throw new Error("Power House PHA binding mismatch");
    const replay = await verifyRootprint(proof.rootprint);
    report.rootprintValid = true;
    report.replayValid = replay === proof.replay_fingerprint;
    if (!report.replayValid) throw new Error("Rootprint replay fingerprint mismatch");
    report.memoryValid = await verifyMemoryCapsule(proof.memory_capsule);
    if (!report.memoryValid) throw new Error("Memory Capsule mismatch");
  } catch (error) {
    report.errors.push(error instanceof Error ? error.message : String(error));
  }
  return report;
}

export async function calculatePhaFingerprint(artifact: PhaArtifact): Promise<string> {
  const core = {
    embedded_proof: {
      proof: artifact.embedded_proof.proof,
      protocol: artifact.embedded_proof.protocol,
      public_inputs: artifact.embedded_proof.public_inputs,
    },
    provenance: artifact.provenance,
    schema: artifact.schema,
  };
  assertIntegerJson(core);
  return hashDomain(PHA_DOMAIN, encoder.encode(canonicalStringify(core)));
}

export async function verifyWorld(world: DemoWorld): Promise<VerificationReport> {
  const errors: string[] = [];
  let cellsValid = 0;
  let phaValid = 0;
  for (const cell of world.cells) {
    try {
      const chunk = await hashDomain(
        CHUNK_DOMAIN,
        encoder.encode(canonicalStringify(cell.channel_payload)),
      );
      const chunkRoot = await hashDomain(MERKLE_LEAF_DOMAIN, digestBytes(chunk));
      if (
        chunkRoot !== cell.manifest.chunk_merkle_root ||
        cell.manifest.channels.some((channel) => channel.chunk_root !== chunkRoot)
      ) {
        throw new Error(cell.key + ": chunk commitment mismatch");
      }
      const cellId = await calculateCellId(cell.manifest);
      if (cellId !== cell.cell_id) {
        throw new Error(cell.key + ": Cell identity mismatch");
      }
      cellsValid += 1;
      const pha = await calculatePhaFingerprint(cell.proof.pha);
      if (
        pha !== cell.proof.pha.phx_fingerprint ||
        cell.proof.pha.embedded_proof.protocol !== "tessaryn/world-cell/v0" ||
        cell.proof.pha.embedded_proof.public_inputs.cell_manifest_digest !== cell.cell_id ||
        cell.proof.pha.identity_root !== cell.proof.rootprint_id
      ) {
        throw new Error(cell.key + ": PHA binding mismatch");
      }
      phaValid += 1;
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error));
    }
  }

  let rootprintValid = false;
  let replayValid = false;
  let memoryValid = false;
  try {
    const replay = await verifyRootprint(world.lineage.rootprint);
    rootprintValid = true;
    replayValid = replay === world.lineage.replay_fingerprint;
    if (!replayValid) errors.push("Rootprint replay fingerprint mismatch");
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }
  try {
    memoryValid = await verifyMemoryCapsule(world.origin_memory_capsule);
    if (!memoryValid) errors.push("Memory Capsule mismatch");
  } catch (error) {
    errors.push(error instanceof Error ? error.message : String(error));
  }
  return {
    cellsValid,
    phaValid,
    rootprintValid,
    replayValid,
    memoryValid,
    disputedCells: world.cells.filter((cell) => cell.manifest.evidence.disputed).length,
    restrictedCells: world.cells.filter((cell) => cell.manifest.evidence.restricted).length,
    errors,
  };
}

export async function verifyReconstructionArtifact(
  artifact: ReconstructionArtifactView,
): Promise<ReconstructionBrowserReport> {
  const result: ReconstructionBrowserReport = {
    cellsValid: 0,
    phaValid: 0,
    rootprintValid: false,
    replayValid: false,
    memoryValid: false,
    reportValid: false,
    rawFramesAbsent: false,
    surfels: [],
    sdfVoxels: [],
    voxelSizeUm: 0,
    voxels: 0,
    errors: [],
  };
  try {
    if (artifact.schema !== "tessaryn/reconstruction-artifact/v0") {
      throw new Error("unsupported reconstruction artifact schema");
    }
    assertIntegerJson(artifact);
    const report = artifact.report;
    if (report.raw_frames_embedded || report.observation.raw_embedded) {
      throw new Error("raw frame disclosure boundary failed");
    }
    result.rawFramesAbsent = true;

    const surfelBytes = bytesFromBase64(report.observation.public_chunk, "surfel chunk");
    result.surfels = decodeSurfelChunk(surfelBytes);
    const observationChunkId = await hashDomain(CHUNK_DOMAIN, surfelBytes);
    const observationRoot = await hashDomain(
      MERKLE_LEAF_DOMAIN,
      digestBytes(observationChunkId),
    );
    if (
      observationChunkId !== report.observation.public_chunk_id ||
      observationRoot !== report.observation.manifest.chunk_merkle_root ||
      report.observation.manifest.channels.some(
        (channel) => channel.chunk_root !== observationRoot,
      ) ||
      result.surfels.length !== report.observation.accepted_samples ||
      (await calculateCellId(report.observation.manifest)) !== report.observation.cell_id
    ) {
      throw new Error("observation Cell or surfel commitment mismatch");
    }
    const forgeProjection = {
      accepted_samples: report.observation.accepted_samples,
      cell_id: report.observation.cell_id,
      excluded_samples: report.observation.excluded_samples,
      public_chunk_id: report.observation.public_chunk_id,
      publication_allowed: report.observation.publication_allowed,
      raw_embedded: false,
    };
    if (
      (await hashDomain(CHUNK_DOMAIN, encoder.encode(canonicalStringify(forgeProjection)))) !==
      report.observation.report_id
    ) {
      throw new Error("Forge report identity mismatch");
    }
    result.cellsValid += 1;

    const sdfBytes = bytesFromBase64(report.sdf_chunk, "SDF chunk");
    const sdf = decodeSdfChunk(sdfBytes, artifact.reconstruction_policy.voxel_size_um);
    result.sdfVoxels = sdf.voxels;
    result.voxelSizeUm = sdf.voxelSizeUm;
    result.voxels = sdf.voxels.length;
    const sdfChunkId = await hashDomain(CHUNK_DOMAIN, sdfBytes);
    const sdfRoot = await hashDomain(MERKLE_LEAF_DOMAIN, digestBytes(sdfChunkId));
    if (
      sdfChunkId !== report.sdf_chunk_id ||
      sdfRoot !== report.sdf_manifest.chunk_merkle_root ||
      report.sdf_manifest.channels.some((channel) => channel.chunk_root !== sdfRoot) ||
      (await calculateCellId(report.sdf_manifest)) !== report.sdf_cell_id ||
      report.sdf_manifest.parents.length !== 1 ||
      report.sdf_manifest.parents[0] !== report.observation.cell_id ||
      result.voxels !== report.fused_voxels
    ) {
      throw new Error("derived SDF Cell or chunk commitment mismatch");
    }
    result.cellsValid += 1;

    const reconstructionProjection = {
      admitted_depth_samples: report.admitted_depth_samples,
      capture_commitment: report.capture_commitment,
      fused_voxels: report.fused_voxels,
      masked_depth_samples: report.masked_depth_samples,
      observation_cell: report.observation.cell_id,
      raw_frames_embedded: false,
      reconstruction: artifact.reconstruction_policy,
      sdf_cell: report.sdf_cell_id,
      sdf_chunk: report.sdf_chunk_id,
    };
    if (
      (await hashDomain(
        CHUNK_DOMAIN,
        encoder.encode(canonicalStringify(reconstructionProjection)),
      )) !== report.report_id
    ) {
      throw new Error("reconstruction report identity mismatch");
    }
    result.reportValid = true;

    for (const [name, proof, expectedCell] of [
      ["observation", artifact.observation_proof, report.observation.cell_id],
      ["SDF", artifact.sdf_proof, report.sdf_cell_id],
    ] as const) {
      if (
        proof.cell_id !== expectedCell ||
        (await calculateCellId(proof.manifest)) !== expectedCell ||
        (await calculatePhaFingerprint(proof.pha)) !== proof.pha.phx_fingerprint ||
        proof.pha.embedded_proof.protocol !== "tessaryn/world-cell/v0" ||
        proof.pha.embedded_proof.public_inputs.cell_manifest_digest !== expectedCell
      ) {
        throw new Error(name + " Power House Cell binding mismatch");
      }
      result.phaValid += 1;
      const replay = await verifyRootprint(proof.rootprint);
      if (replay !== proof.replay_fingerprint) {
        throw new Error(name + " Rootprint replay mismatch");
      }
      if (!(await verifyMemoryCapsule(proof.memory_capsule))) {
        throw new Error(name + " Memory Capsule mismatch");
      }
    }
    result.memoryValid = true;

    const lineageReplay = await verifyRootprint(artifact.lineage.rootprint);
    result.rootprintValid = true;
    result.replayValid = lineageReplay === artifact.lineage.replay_fingerprint;
    if (!result.replayValid) throw new Error("world lineage replay mismatch");
    const graphIds = Object.keys(artifact.lineage.rootprint.branches).sort(compareUtf8);
    const mappedIds = Object.values(artifact.lineage.branches).sort(compareUtf8);
    if (canonicalStringify(graphIds) !== canonicalStringify(mappedIds)) {
      throw new Error("world lineage branch map mismatch");
    }
    for (const [label, expectedCell] of [
      ["observation", report.observation.cell_id],
      ["sdf-derived", report.sdf_cell_id],
    ] as const) {
      const branchId = artifact.lineage.branches[label];
      const branch = branchId ? artifact.lineage.rootprint.branches[branchId] : undefined;
      if (branch?.artifact.embedded_proof.public_inputs.cell_manifest_digest !== expectedCell) {
        throw new Error(label + " lineage binding mismatch");
      }
    }
    if (
      !Object.values(artifact.observation_proof_report).every(
        (value) => value === true || value === false,
      ) ||
      artifact.observation_proof_report.physical_truth_claimed !== false ||
      artifact.sdf_proof_report.physical_truth_claimed !== false ||
      artifact.verification.observation_valid !== true ||
      artifact.verification.sdf_valid !== true ||
      artifact.verification.report_valid !== true ||
      artifact.verification.raw_frames_absent !== true ||
      artifact.verification.verified_surfels !== result.surfels.length ||
      artifact.verification.verified_voxels !== result.voxels
    ) {
      throw new Error("stored verification report mismatch");
    }
  } catch (error) {
    result.errors.push(error instanceof Error ? error.message : String(error));
  }
  return result;
}

export async function verifyValidationLocusArtifact(
  artifact: ValidationLocusArtifactView,
): Promise<ValidationLocusBrowserReport> {
  const result: ValidationLocusBrowserReport = {
    cellsValid: 0,
    phaValid: 0,
    rootprintValid: false,
    replayValid: false,
    memoryValid: false,
    moments: [],
    alternate: null,
    sourceManifest: artifact.source.source_manifest,
    errors: [],
  };
  try {
    assertIntegerJson(artifact);
    if (
      artifact.schema !== "tessaryn/validation-locus-artifact/v1" ||
      artifact.moments.length !== 3 ||
      canonicalStringify(artifact.source.profile) !== canonicalStringify(TARTANAIR_PROFILE) ||
      artifact.source.selected_frames <= 0
    ) {
      throw new Error("invalid validation Locus envelope");
    }
    const expectedMomentIds = ["moment-a", "moment-b", "moment-c"];
    if (
      artifact.moments.some((moment, index) => moment.id !== expectedMomentIds[index]) ||
      artifact.alternate.id !== "alternate-c" ||
      !artifact.moments
        .slice(1)
        .every(
          (moment, index) =>
            moment.captured_at_unix_us >
            (artifact.moments[index]?.captured_at_unix_us ?? Number.MAX_SAFE_INTEGER),
        )
    ) {
      throw new Error("validation Moment ordering mismatch");
    }

    const temporalStates = [...artifact.moments, artifact.alternate];
    const expectedSelectionIds = [...expectedMomentIds, "alternate-c"];
    const selectedFrames = artifact.source.selections.reduce(
      (total, selection) => total + selection.frame_ids.length,
      0,
    );
    if (
      artifact.source.selections.length !== expectedSelectionIds.length ||
      selectedFrames !== artifact.source.selected_frames ||
      artifact.source.selections.some((selection, index) => {
        const expectedMoment = temporalStates[index];
        return (
          selection.id !== expectedSelectionIds[index] ||
          selection.frame_ids.length < 3 ||
          selection.frame_ids.length !== selection.captured_at_unix_us.length ||
          selection.frame_ids.length !== selection.source_indices.length ||
          selection.frame_ids.some((digest) => !/^sha256:[0-9a-f]{64}$/u.test(digest)) ||
          selection.source_indices.some(
            (sourceIndex, sourcePosition) =>
              !Number.isSafeInteger(sourceIndex) ||
              sourceIndex < 0 ||
              sourceIndex * 100_000 !== selection.captured_at_unix_us[sourcePosition],
          ) ||
          selection.captured_at_unix_us.some(
            (timestamp, timestampIndex) =>
              timestampIndex > 0 &&
              timestamp <= (selection.captured_at_unix_us[timestampIndex - 1] ?? timestamp),
          ) ||
          selection.captured_at_unix_us[0] !== expectedMoment?.captured_at_unix_us
        );
      })
    ) {
      throw new Error("validation source selection mismatch");
    }
    const selectionProjection = {
      profile: artifact.source.profile,
      selected: artifact.source.selections,
    };
    const calculatedSelection = await hashDomain(
      CHUNK_DOMAIN,
      encoder.encode(canonicalStringify(selectionProjection)),
    );
    if (calculatedSelection !== artifact.source.selection_manifest) {
      throw new Error("validation selection manifest mismatch");
    }

    const sourceProjection = {
      moments: [...artifact.moments, artifact.alternate].map((moment) => ({
        capture_commitment: moment.artifact.report.capture_commitment,
        id: moment.id,
        observation_cell: moment.artifact.report.observation.cell_id,
        sdf_cell: moment.artifact.report.sdf_cell_id,
      })),
      profile: artifact.source.profile,
      selection_manifest: artifact.source.selection_manifest,
    };
    const sourceBytes = encoder.encode(canonicalStringify(sourceProjection));
    const calculatedSource = await hashDomain(
      CHUNK_DOMAIN,
      sourceBytes,
    );
    if (calculatedSource !== artifact.source.source_manifest) {
      throw new Error("validation source manifest mismatch");
    }
    const sourceRoot = await hashDomain(MERKLE_LEAF_DOMAIN, digestBytes(calculatedSource));
    const sourceProof = artifact.source_proof;
    const sourceChannel = sourceProof.manifest.channels[0];
    const expectedSourceParents = [
      artifact.moments[2]!.artifact.report.sdf_cell_id,
      artifact.alternate.artifact.report.sdf_cell_id,
    ].sort(compareUtf8);
    if (
      sourceProof.manifest.class !== "aggregate" ||
      sourceProof.manifest.channels.length !== 1 ||
      sourceChannel?.role !== "reconstruction/report" ||
      sourceChannel.codec !== "tessaryn/validation-source-manifest" ||
      sourceChannel.codec_version !== "1" ||
      sourceChannel.license !== artifact.source.profile.license ||
      sourceChannel.chunk_root !== sourceRoot ||
      sourceChannel.uncompressed_bytes !== sourceBytes.length ||
      sourceProof.manifest.chunk_merkle_root !== sourceRoot ||
      canonicalStringify([...sourceProof.manifest.parents].sort(compareUtf8)) !==
        canonicalStringify(expectedSourceParents) ||
      (await calculateCellId(sourceProof.manifest)) !== sourceProof.cell_id ||
      (await calculatePhaFingerprint(sourceProof.pha)) !== sourceProof.pha.phx_fingerprint ||
      sourceProof.pha.embedded_proof.public_inputs.cell_manifest_digest !==
        sourceProof.cell_id
    ) {
      throw new Error("validation source Cell or PHA binding mismatch");
    }
    const sourceReplay = await verifyRootprint(sourceProof.rootprint);
    if (sourceReplay !== sourceProof.replay_fingerprint) {
      throw new Error("validation source Cell replay mismatch");
    }
    const sourceMemoryValid = await verifyMemoryCapsule(sourceProof.memory_capsule);
    if (
      !sourceMemoryValid ||
      artifact.source_proof_report.cell_identity_valid !== true ||
      artifact.source_proof_report.pha_valid !== true ||
      artifact.source_proof_report.rootprint_valid !== true ||
      artifact.source_proof_report.replay_valid !== true ||
      artifact.source_proof_report.memory_capsule_valid !== true ||
      artifact.source_proof_report.physical_truth_claimed !== false
    ) {
      throw new Error("validation source Cell verification report mismatch");
    }
    result.cellsValid += 1;
    result.phaValid += 1;

    const verified = [];
    for (const moment of artifact.moments) {
      const verification = await verifyReconstructionArtifact(moment.artifact);
      if (verification.errors.length > 0) {
        throw new Error(`${moment.id}: ${verification.errors.join(" / ")}`);
      }
      result.cellsValid += verification.cellsValid;
      result.phaValid += verification.phaValid;
      verified.push({
        id: moment.id,
        label: moment.label,
        capturedAtUnixUs: moment.captured_at_unix_us,
        verification,
      });
    }
    const alternateVerification = await verifyReconstructionArtifact(
      artifact.alternate.artifact,
    );
    if (alternateVerification.errors.length > 0) {
      throw new Error(`alternate-c: ${alternateVerification.errors.join(" / ")}`);
    }
    result.cellsValid += alternateVerification.cellsValid;
    result.phaValid += alternateVerification.phaValid;
    result.moments = verified;
    result.alternate = {
      id: artifact.alternate.id,
      label: artifact.alternate.label,
      capturedAtUnixUs: artifact.alternate.captured_at_unix_us,
      verification: alternateVerification,
    };
    result.memoryValid =
      sourceMemoryValid &&
      [...verified.map((moment) => moment.verification), alternateVerification].every(
        (verification) => verification.memoryValid,
      );

    const replay = await verifyRootprint(artifact.lineage.rootprint);
    result.rootprintValid = true;
    result.replayValid = replay === artifact.lineage.replay_fingerprint;
    if (!result.replayValid) throw new Error("validation Rootprint replay mismatch");
    const sourceBranchId = artifact.lineage.branches.source;
    const sourceBranch = sourceBranchId
      ? artifact.lineage.rootprint.branches[sourceBranchId]
      : undefined;
    const expectedSourceBranches = [
      artifact.lineage.branches["moment-c-sdf"]!,
      artifact.lineage.branches["alternate-c-sdf"]!,
    ].sort(compareUtf8);
    if (
      !sourceBranch ||
      canonicalStringify([...sourceBranch.parents].sort(compareUtf8)) !==
        canonicalStringify(expectedSourceBranches)
    ) {
      throw new Error("validation source branch merge mismatch");
    }

    const bindings = [
      ["source", artifact.source_proof.cell_id] as const,
      ...artifact.moments.flatMap((moment) => [
        [`${moment.id}-observation`, moment.artifact.report.observation.cell_id] as const,
        [`${moment.id}-sdf`, moment.artifact.report.sdf_cell_id] as const,
      ]),
      [
        "alternate-c-observation",
        artifact.alternate.artifact.report.observation.cell_id,
      ] as const,
      ["alternate-c-sdf", artifact.alternate.artifact.report.sdf_cell_id] as const,
    ];
    for (const [label, cellId] of bindings) {
      const branchId = artifact.lineage.branches[label];
      const branch = branchId ? artifact.lineage.rootprint.branches[branchId] : undefined;
      if (
        !branch ||
        branch.artifact.embedded_proof.public_inputs.cell_manifest_digest !== cellId
      ) {
        throw new Error(`${label}: validation lineage binding mismatch`);
      }
    }
  } catch (error) {
    result.errors.push(error instanceof Error ? error.message : String(error));
  }
  return result;
}

function decodeSurfelChunk(bytes: Uint8Array): ReconstructionBrowserReport["surfels"] {
  const header = SURFEL_MAGIC.length + 4;
  if (!hasMagic(bytes, SURFEL_MAGIC) || bytes.length < header) {
    throw new Error("malformed surfel chunk");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const count = view.getUint32(SURFEL_MAGIC.length, true);
  if (count > 1_000_000 || bytes.length !== header + count * 40) {
    throw new Error("surfel chunk resource or length mismatch");
  }
  const surfels: ReconstructionBrowserReport["surfels"] = [];
  let cursor = header;
  for (let index = 0; index < count; index += 1) {
    const position = [0, 0, 0] as [number, number, number];
    for (let axis = 0; axis < 3; axis += 1) {
      const coordinate = view.getBigInt64(cursor, true);
      cursor += 8;
      const numeric = Number(coordinate);
      if (!Number.isSafeInteger(numeric)) throw new Error("surfel coordinate is unsafe");
      position[axis] = numeric;
    }
    const normalQ15 = [
      view.getInt16(cursor, true),
      view.getInt16(cursor + 2, true),
      view.getInt16(cursor + 4, true),
    ] as [number, number, number];
    cursor += 6;
    const color = [
      view.getUint8(cursor),
      view.getUint8(cursor + 1),
      view.getUint8(cursor + 2),
      view.getUint8(cursor + 3),
    ] as [number, number, number, number];
    cursor += 4;
    const radiusUm = view.getUint32(cursor, true);
    cursor += 4;
    const confidence = view.getUint16(cursor, true);
    cursor += 2;
    if (radiusUm === 0 || confidence > 10_000) throw new Error("invalid surfel sample");
    surfels.push({ positionUm: position, normalQ15, color, radiusUm });
  }
  return surfels;
}

function decodeSdfChunk(
  bytes: Uint8Array,
  expectedVoxelSize: number,
): { voxelSizeUm: number; voxels: ReconstructionBrowserReport["sdfVoxels"] } {
  const header = SDF_MAGIC.length + 8;
  if (!hasMagic(bytes, SDF_MAGIC) || bytes.length < header) {
    throw new Error("malformed sparse SDF chunk");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const voxelSize = view.getUint32(SDF_MAGIC.length, true);
  const count = view.getUint32(SDF_MAGIC.length + 4, true);
  if (
    voxelSize !== expectedVoxelSize ||
    count > 2_000_000 ||
    bytes.length !== header + count * 20
  ) {
    throw new Error("sparse SDF dimensions mismatch");
  }
  let cursor = header;
  let previous: [number, number, number] | null = null;
  const voxels: ReconstructionBrowserReport["sdfVoxels"] = [];
  for (let index = 0; index < count; index += 1) {
    const coordinate = [
      view.getInt32(cursor, true),
      view.getInt32(cursor + 4, true),
      view.getInt32(cursor + 8, true),
    ] as [number, number, number];
    cursor += 12;
    const signedDistanceUm = view.getInt32(cursor, true);
    cursor += 4;
    const weight = view.getUint32(cursor, true);
    cursor += 4;
    if (weight === 0 || (previous && compareCoordinate(previous, coordinate) >= 0)) {
      throw new Error("noncanonical sparse SDF voxel");
    }
    previous = coordinate;
    voxels.push({ coordinate, signedDistanceUm, weight });
  }
  return { voxelSizeUm: voxelSize, voxels };
}

function compareCoordinate(
  left: [number, number, number],
  right: [number, number, number],
): number {
  return left[0] - right[0] || left[1] - right[1] || left[2] - right[2];
}

function bytesFromBase64(encoded: string, label: string): Uint8Array {
  if (!/^[A-Za-z0-9+/]*$/.test(encoded) || encoded.length % 4 === 1) {
    throw new Error(label + " contains noncanonical Base64");
  }
  const padding = "=".repeat((4 - (encoded.length % 4)) % 4);
  let binary: string;
  try {
    binary = atob(encoded + padding);
  } catch {
    throw new Error(label + " contains invalid Base64");
  }
  if (btoa(binary).replace(/=+$/u, "") !== encoded) {
    throw new Error(label + " contains noncanonical Base64");
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function hasMagic(bytes: Uint8Array, magic: Uint8Array): boolean {
  return (
    bytes.length >= magic.length && magic.every((value, index) => bytes[index] === value)
  );
}

export async function runMutation(
  world: DemoWorld,
  selected: DemoCell,
  mutation: string,
  semanticCapsule: Record<string, any> = world.origin_memory_capsule,
): Promise<RejectionResult> {
  if (mutation === "coordinate") {
    const manifest = structuredClone(selected.manifest);
    manifest.spatial_extent.max_um[0] += 1;
    const actual = await calculateCellId(manifest);
    return {
      id: "mut_cell_coordinate_001",
      expectedLayer: "cell",
      actualLayer: actual === selected.cell_id ? "none" : "cell",
      code: actual === selected.cell_id ? "UNEXPECTED_ACCEPT" : "CELL_ID_MISMATCH",
      coreUnchanged: false,
      detail: "One canonical micrometer changed; Cell identity no longer matches.",
    };
  }
  if (mutation === "fingerprint") {
    const artifact = structuredClone(selected.proof.pha);
    artifact.embedded_proof.proof.identity_verified = false;
    const actual = await calculatePhaFingerprint(artifact);
    return {
      id: "mut_pha_core_001",
      expectedLayer: "core",
      actualLayer: actual === artifact.phx_fingerprint ? "none" : "core",
      code: actual === artifact.phx_fingerprint ? "UNEXPECTED_ACCEPT" : "PHA_CORE_INVALID",
      coreUnchanged: false,
      detail: "The proof payload changed while the stored Power House fingerprint did not.",
    };
  }
  if (mutation === "semantic") {
    const capsule = structuredClone(semanticCapsule);
    const packet = capsule.semantics?.packets?.[0];
    if (!packet?.packet) throw new Error("semantic packet unavailable");
    packet.packet.summary = "tampered semantic presentation";
    const actual = await semanticPacketDigest(packet.packet);
    return {
      id: "mut_semantic_note_001",
      expectedLayer: "semantic",
      actualLayer: actual === packet.packet_digest ? "none" : "semantic",
      code: actual === packet.packet_digest ? "UNEXPECTED_ACCEPT" : "PACKET_DIGEST_MISMATCH",
      coreUnchanged: true,
      detail: "Meaning changed and was rejected; Cell and PHA identities remain valid.",
    };
  }
  throw new Error("unsupported mutation: " + mutation);
}

async function verifyMemoryCapsule(capsule: Record<string, any>): Promise<boolean> {
  if (capsule.header?.schema !== "power-house/memory-capsule/v1") return false;
  const projection = structuredClone(capsule);
  projection.header.capsule_digest = null;
  const capsuleDigest = await hashDomain(
    CAPSULE_DOMAIN,
    encoder.encode(canonicalStringify(projection)),
  );
  if (capsuleDigest !== capsule.header.capsule_digest) return false;

  const coreProjection = {
    core_verification_policy: capsule.core.core_verification_policy,
    pha: capsule.core.pha,
    proofs: capsule.core.proofs,
  };
  const coreDigest = await hashDomain(
    CORE_DOMAIN,
    encoder.encode(canonicalStringify(coreProjection)),
  );
  if (
    coreDigest !== capsule.core.core_digest ||
    (await calculatePhaFingerprint(capsule.core.pha)) !== capsule.core.pha.phx_fingerprint
  ) {
    return false;
  }

  const replay = await verifyRootprint(capsule.lineage.rootprint);
  if (replay !== capsule.replay.replay.expected.replay_fingerprint) return false;

  if (capsule.semantics) {
    const sidecar = capsule.semantics.sidecar;
    if (!sidecar || sidecar.rootprint_state_fingerprint !== replay) return false;
    const sidecarProjection = {
      nodes: sidecar.nodes,
      rootprint_state_fingerprint: sidecar.rootprint_state_fingerprint,
      schema: sidecar.schema,
    };
    const sidecarDigest = await hashDomain(
      SIDECAR_DOMAIN,
      encoder.encode(canonicalStringify(sidecarProjection)),
    );
    if (sidecarDigest !== sidecar.sidecar_sha256) return false;
    for (const packet of capsule.semantics.packets ?? []) {
      if (
        packet.bound_replay_fingerprint !== replay ||
        !capsule.lineage.rootprint.branches[packet.bound_branch_id] ||
        !packet.packet ||
        (await semanticPacketDigest(packet.packet)) !== packet.packet_digest
      ) {
        return false;
      }
    }
  }
  return true;
}

async function semanticPacketDigest(packet: Record<string, any>): Promise<string> {
  const projection = structuredClone(packet);
  if ("packet_digest" in projection) projection.packet_digest = "";
  if (projection.digests && "packet" in projection.digests) projection.digests.packet = "";
  if (projection.digests && "packet_digest" in projection.digests) {
    projection.digests.packet_digest = "";
  }
  return hashDomain(SEMANTIC_DOMAIN, encoder.encode(canonicalStringify(projection)));
}

async function verifyRootprint(graph: Rootprint): Promise<string> {
  if (graph.schema !== "power-house/rootprint/v1") {
    throw new Error("unsupported Rootprint schema");
  }
  const root = graph.branches[graph.root_branch];
  if (!root || root.sequence !== 0 || root.parents.length !== 0) {
    throw new Error("invalid Rootprint root");
  }
  for (const [key, branch] of Object.entries(graph.branches)) {
    if (key !== branch.id || branch.parents.length > 2) {
      throw new Error("invalid Rootprint branch " + key);
    }
    if ((await calculatePhaFingerprint(branch.artifact)) !== branch.artifact.phx_fingerprint) {
      throw new Error("invalid PHA in Rootprint branch " + key);
    }
    const expected = await calculateBranchId(branch);
    if (expected !== branch.id) throw new Error("branch ID mismatch " + key);
    for (const parent of branch.parents) {
      const parentBranch = graph.branches[parent];
      if (!parentBranch || parentBranch.sequence >= branch.sequence) {
        throw new Error("invalid parent ordering " + key);
      }
    }
  }
  detectCycles(graph);

  let ordered = Object.values(graph.branches).sort(
    (left, right) => left.sequence - right.sequence || compareUtf8(left.id, right.id),
  );
  const canonicalSequences = new Map<string, number>();
  for (const branch of ordered) {
    const parentSequence = branch.parents.reduce(
      (maximum, parent) => Math.max(maximum, canonicalSequences.get(parent) ?? 0),
      0,
    );
    canonicalSequences.set(branch.id, parentSequence + (branch.parents.length > 0 ? 1 : 0));
  }
  ordered = ordered.sort(
    (left, right) =>
      (canonicalSequences.get(left.id) ?? 0) - (canonicalSequences.get(right.id) ?? 0) ||
      compareUtf8(left.id, right.id),
  );
  const replayBranches = ordered.map((branch) => ({
    artifact_phx_fingerprint: branch.artifact.phx_fingerprint,
    id: branch.id,
    label: branch.label,
    parents: branch.parents,
    sequence: canonicalSequences.get(branch.id) ?? 0,
  }));
  const parentIds = new Set(ordered.flatMap((branch) => branch.parents));
  const tips = (Object.keys(graph.branches) as RootprintBranch["id"][])
    .filter((id) => !parentIds.has(id))
    .sort(compareUtf8);
  return hashDomain(
    REPLAY_DOMAIN,
    encoder.encode(
      canonicalStringify({
        branches: replayBranches,
        root_branch: graph.root_branch,
        tips,
      }),
    ),
  );
}

async function calculateBranchId(branch: RootprintBranch): Promise<string> {
  return hashDomain(
    BRANCH_DOMAIN,
    encoder.encode(
      canonicalStringify({
        artifact_phx_fingerprint: branch.artifact.phx_fingerprint,
        label: branch.label,
        parents: branch.parents,
      }),
    ),
  );
}

function detectCycles(graph: Rootprint): void {
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (id: string): void => {
    if (visiting.has(id)) throw new Error("Rootprint cycle detected");
    if (visited.has(id)) return;
    visiting.add(id);
    for (const parent of graph.branches[id]?.parents ?? []) visit(parent);
    visiting.delete(id);
    visited.add(id);
  };
  for (const id of Object.keys(graph.branches)) visit(id);
}

function canonicalizeManifest(manifest: CellManifest): CellManifest {
  const value = structuredClone(manifest);
  value.channels.sort(
    (left, right) =>
      compareUtf8(left.role, right.role) || compareUtf8(left.chunk_root, right.chunk_root),
  );
  value.parents = [...new Set(value.parents)].sort(compareUtf8);
  value.temporal_extent.supersedes = [...new Set(value.temporal_extent.supersedes)].sort(
    compareUtf8,
  );
  value.source_records.sort((left, right) => compareUtf8(left.source_id, right.source_id));
  value.transform_records.sort((left, right) =>
    compareUtf8(left.transform_id, right.transform_id),
  );
  for (const transform of value.transform_records) {
    transform.input_ids = [...new Set(transform.input_ids)].sort(compareUtf8);
  }
  assertIntegerJson(value);
  return value;
}

function canonicalStringify(value: unknown): string {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) throw new Error("non-integer or unsafe JSON number");
    return String(value);
  }
  if (Array.isArray(value)) {
    return "[" + value.map(canonicalStringify).join(",") + "]";
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => compareUtf8(left, right));
    return (
      "{" +
      entries
        .map(([key, item]) => JSON.stringify(key) + ":" + canonicalStringify(item))
        .join(",") +
      "}"
    );
  }
  throw new Error("unsupported canonical JSON type: " + typeof value);
}

function compareUtf8(left: string, right: string): number {
  const leftBytes = encoder.encode(left);
  const rightBytes = encoder.encode(right);
  const length = Math.min(leftBytes.length, rightBytes.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (leftBytes[index] ?? 0) - (rightBytes[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return leftBytes.length - rightBytes.length;
}

function assertIntegerJson(value: unknown): void {
  if (typeof value === "number" && !Number.isSafeInteger(value)) {
    throw new Error("identity-bearing JSON contains a non-integer number");
  }
  if (Array.isArray(value)) {
    for (const item of value) assertIntegerJson(item);
  } else if (value && typeof value === "object") {
    for (const item of Object.values(value as Record<string, unknown>)) {
      assertIntegerJson(item);
    }
  }
}

function digestBytes(value: string): Uint8Array {
  if (!/^sha256:[0-9a-f]{64}$/.test(value)) throw new Error("invalid SHA-256 digest");
  const bytes = new Uint8Array(32);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(value.slice(7 + index * 2, 9 + index * 2), 16);
  }
  return bytes;
}

async function hashDomain(domain: Uint8Array, payload: Uint8Array): Promise<string> {
  const bytes = new Uint8Array(domain.length + payload.length);
  bytes.set(domain);
  bytes.set(payload, domain.length);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return (
    "sha256:" +
    Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")
  );
}

async function hashDomainBytes(
  domain: Uint8Array,
  payload: Uint8Array,
): Promise<Uint8Array> {
  const bytes = concatenate([domain, payload]);
  const input = new Uint8Array(bytes.byteLength);
  input.set(bytes);
  return new Uint8Array(await crypto.subtle.digest("SHA-256", input.buffer));
}

function concatenate(parts: readonly Uint8Array[]): Uint8Array {
  const length = parts.reduce((total, part) => total + part.byteLength, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.byteLength;
  }
  return output;
}

function compareBytes(left: Uint8Array, right: Uint8Array): number {
  const length = Math.min(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (left[index] ?? 0) - (right[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return left.length - right.length;
}

function toHex(value: Uint8Array): string {
  return Array.from(value, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
